import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import type { QuotePriceUnit, RestaurantInquiryQuote } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// PATCH .../items/:itemId/quotes/:quoteId — edit ONE quote in place:
//   • price, priceUnit, notes and masterProductId are editable; send only what
//     changed. The supplier never changes.
//   • priceUnit must name a rung the quoted master product defines (PACK needs
//     a packUnit, ITEM an itemUnit), otherwise 400.
//   • 400 "Cannot edit a quote on a terminal-state item" once the item is
//     decided; only PENDING quotes on live items are editable.
//   • syncSupplierPrice: true also upserts supplier_prices with the quote's
//     (updated) price; the stored price must be > 0 (400 otherwise).
// Returns the updated quote (with the product summary).
type TData = RestaurantInquiryQuote;
type TError = AxiosError;
type TVariables = {
  id: number | string;
  itemId: number;
  quoteId: number;
  price?: number;
  priceUnit?: QuotePriceUnit;
  notes?: string;
  masterProductId?: number;
  syncSupplierPrice?: boolean;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useUpdateInquiryItemQuoteApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({
    id,
    itemId,
    quoteId,
    price,
    priceUnit,
    notes,
    masterProductId,
    syncSupplierPrice,
  }: TVariables): Promise<TData> => {
    const body: Record<string, unknown> = {};
    if (price != null) body.price = price;
    if (priceUnit != null) body.priceUnit = priceUnit;
    if (notes != null) body.notes = notes;
    if (masterProductId != null) body.masterProductId = masterProductId;
    if (syncSupplierPrice != null) body.syncSupplierPrice = syncSupplierPrice;
    const response = await http.patch(`${restaurantInquiriesEndpoint}/${id}/items/${itemId}/quotes/${quoteId}`, body);
    return response.data?.data ?? response.data;
  };

  return useMutationWrapper<TData, TError, TVariables>(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint, 'update-quote'],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
